/** Detail panel for a clicked period. Shows a labeled P&L bar chart at
 * one-finer granularity so "which sub-period was high or low" is obvious:
 *   year  -> a bar per month
 *   month -> a bar per day
 *   day   -> a bar per time-of-day slot (5/15/60-min toggle)
 * Reuses the timeseries + exit-comparison report data; no new backend. */

import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { reportsApi, type Granularity, type ReportExecutionMode } from "@/lib/reportsApi";
import { dailyPnlWithVix, periodRange, shortPeriodLabel, timeOfDayBuckets } from "./drilldownMath";
import { PeriodBars, type LabeledBar } from "./PeriodBars";

interface Props {
  strategyId: string;
  period: string;
  granularity: Granularity;
  mode: ReportExecutionMode;
  onClose: () => void;
}

export function DrilldownPanel(props: Readonly<Props>): ReactElement {
  return (
    <div className="mt-4 rounded-xl border border-accent-border bg-surface-1 p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <i className="ti ti-zoom-in text-accent" />
          {title(props.period, props.granularity)}
        </div>
        <button
          type="button"
          className="btn btn-secondary px-2 py-1 text-xs"
          onClick={props.onClose}
        >
          <i className="ti ti-x text-xs" /> Close
        </button>
      </div>
      {props.granularity === "day" && <DayDetail {...props} />}
      {props.granularity === "month" && <MonthDetail {...props} />}
      {props.granularity === "year" && <YearDetail {...props} />}
    </div>
  );
}

/** Month -> a bar per day, each labeled with its weekday and VIX. Uses the
 * exit-comparison data (which carries per-day VIX + trade times). */
function MonthDetail({ strategyId, period, mode }: Readonly<Props>): ReactElement {
  const { from, to } = periodRange(period, "month");
  const q = useQuery({
    queryKey: ["exit-comparison", strategyId, from, to, mode],
    queryFn: () => reportsApi.exitComparison(from, to, strategyId, mode),
  });
  if (q.isPending) return <Loading />;
  if (q.isError) return <LoadError />;
  const bars: LabeledBar[] = dailyPnlWithVix(q.data.trades, q.data.vix).map((d) => ({
    label: d.dayLabel,
    sub: d.weekday,
    vix: d.vix,
    pnl: d.pnl,
  }));
  return (
    <>
      <SubLabel text="P&L by day (weekday · VIX)" />
      <PeriodBars bars={bars} />
    </>
  );
}

/** Year -> a bar per month (via the timeseries API). */
function YearDetail({ strategyId, period, mode }: Readonly<Props>): ReactElement {
  const { from, to } = periodRange(period, "year");
  const q = useQuery({
    queryKey: ["report-timeseries-sub", strategyId, "month", from, to, mode],
    queryFn: () => reportsApi.timeseries("month", strategyId, mode),
  });
  if (q.isPending) return <Loading />;
  if (q.isError) return <LoadError />;
  const bars: LabeledBar[] = q.data.buckets
    .filter((b) => b.period >= from && b.period <= to)
    .map((b) => ({ label: shortPeriodLabel(b.period), pnl: b.pnl }));
  return (
    <>
      <SubLabel text="P&L by month" />
      <PeriodBars bars={bars} />
    </>
  );
}

/** Day -> per-time-of-day-slot bars, with a 5/15/60-min toggle. */
function DayDetail({ strategyId, period, mode }: Readonly<Props>): ReactElement {
  const [slot, setSlot] = useState(15);
  const q = useQuery({
    queryKey: ["exit-comparison", strategyId, period, period, mode],
    queryFn: () => reportsApi.exitComparison(period, period, strategyId, mode),
  });
  if (q.isPending) return <Loading />;
  if (q.isError) return <LoadError />;
  const bars: LabeledBar[] = timeOfDayBuckets(q.data.trades, slot).map((b) => ({
    label: b.label,
    pnl: b.pnl,
  }));
  return (
    <>
      <div className="mb-2 flex items-center justify-between">
        <SubLabel text="P&L by time of day" />
        <SlotToggle value={slot} onChange={setSlot} />
      </div>
      <PeriodBars bars={bars} />
    </>
  );
}

const SLOTS: readonly number[] = [5, 15, 60];

function SlotToggle({
  value,
  onChange,
}: Readonly<{ value: number; onChange: (v: number) => void }>): ReactElement {
  return (
    <div className="flex gap-1" role="group" aria-label="Time slot">
      {SLOTS.map((s) => (
        <button
          key={s}
          type="button"
          aria-pressed={value === s}
          className={`rounded-ctl border px-2 py-0.5 text-[11px] ${
            value === s
              ? "border-accent-border bg-accent-bg text-accent"
              : "border-line bg-surface-2 text-ink-secondary"
          }`}
          onClick={() => {
            onChange(s);
          }}
        >
          {s}m
        </button>
      ))}
    </div>
  );
}

function SubLabel({ text }: Readonly<{ text: string }>): ReactElement {
  return <div className="text-[11px] uppercase tracking-wider text-ink-secondary">{text}</div>;
}

function Loading(): ReactElement {
  return <p className="text-sm text-ink-secondary">Loading period detail…</p>;
}

function LoadError(): ReactElement {
  return <p className="text-sm text-danger">Couldn&apos;t load period detail.</p>;
}

const NOUN: Record<Granularity, string> = { day: "Day", month: "Month", year: "Year" };

function title(period: string, granularity: Granularity): string {
  return `${NOUN[granularity]} · ${period}`;
}
